import * as Sentry from '@sentry/react';
import { scrubSentryEvent } from './lib/sentry_scrub';
import {
  scrubUrlFragmentsFromBreadcrumb,
  scrubUrlFragmentsFromEvent,
  scrubUrlFragmentsFromTransaction,
} from './lib/url_fragment_scrub';

/**
 * The guest shell's `/p#<token>` entry URL must never be session-replayed — ADR-024 rule 4. Pulled
 * out as a pure function so the decision is testable without introspecting `Sentry.init`'s config.
 */
export function isGuestReplayExcludedPath(pathname: string): boolean {
  return pathname === '/p';
}

/**
 * Named and exported (rather than inlined into `Sentry.init` below) so a test can assert the exact
 * wired hooks fire, not just that the pure scrub functions work in isolation — a prior version of
 * this file had a correct `scrubUrlFragmentsFromTransaction` available but never wired it into
 * `beforeSendTransaction`, and a test of the pure function alone would not have caught that.
 */
export const sentryOptions: Sentry.BrowserOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',

  integrations: [
    Sentry.browserTracingIntegration(),
    ...(isGuestReplayExcludedPath(window.location.pathname) ? [] : [Sentry.replayIntegration()]),
  ],

  // Tracing — adjust sample rate for production
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

  // Session Replay — capture 10% of sessions, 100% of sessions with errors
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Only send events when DSN is configured
  enabled: !!import.meta.env.VITE_SENTRY_DSN,

  // ADR-024 rule 4: redact the URL fragment (the guest token) off navigation and fetch/xhr
  // breadcrumbs as they are recorded, for every path — not just /p, since a breadcrumb recorded
  // on /p can still be attached to an event sent later from a different path in the same session.
  beforeBreadcrumb: (breadcrumb) => scrubUrlFragmentsFromBreadcrumb(breadcrumb),

  // EW-T18: defence in depth. Strip submitted personal data (PAN, Aadhaar, names, addresses,
  // client-input values, graphql-request payloads) from every event, whatever produced it.
  // ADR-024 rule 5 composes on top: redact any URL fragment still present on `request.url`,
  // `transaction`, or a breadcrumb that predates the `beforeBreadcrumb` hook above.
  beforeSend: (event, hint) => scrubUrlFragmentsFromEvent(scrubSentryEvent(event, hint)),

  // ADR-024 rule 5 (transaction half): `beforeSend` is never called for `type: 'transaction'`
  // events — browserTracingIntegration routes those through this separate hook — so the fragment
  // scrub needs its own pass over `transaction`, `request.url`, and every span's `http.url`.
  beforeSendTransaction: (event) => scrubUrlFragmentsFromTransaction(event),
};

Sentry.init(sentryOptions);
